import { toast } from "@/components/common/Toast/Toast"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import { MapPin, Search, X } from "lucide-react"
import { useEffect, useState } from "react"
import { useCreateStatement } from "../../../../../api/declarationsApi"
import type { CandidateArticle } from "../../../../../api/types"
import type { Verdict } from "../../studio/studioTypes"
import type { EvidencePick } from "../document/evidenceSelection"
import { articleMatches } from "../scope/articleSearch"
import { VerdictSelect } from "./VerdictSelect"

/** A selection the reviewer confirmed in the document (the floating chip),
 *  handed to this dialog from outside. The `token` makes every hand-off
 *  distinct — picking the same passage twice is two gestures. */
export interface ExternalEvidencePick {
  pick: EvidencePick
  token: number
}

export interface AddStatementControlProps {
  declId: string
  /** The declaration's own frameworks (interface groups ∪ detections), like
   *  V1's `addableFrameworks` — a full catalog pick is out of scope. */
  frameworks: string[]
  /** The framework the mask is on — the dialog's preselected Verordnung. Null on
   *  a declaration with no regulation yet: the reviewer must pick one. */
  activeFramework: string | null
  nameOf: (framework: string) => string
  pool: CandidateArticle[]
  /** A workbook declaration has no selectable text — the Fundstelle section
   *  is simply not offered (the statement itself stays fully creatable). */
  isWorkbook: boolean
  onCreated: (framework: string, statementId: string) => void
  /** A selection confirmed in the document: opens this dialog with the pick
   *  as its Fundstelle, or — when the dialog is already open — replaces the
   *  Fundstelle in place. The dialog is NON-MODAL exactly so this can happen
   *  while it stands open. */
  externalPick?: ExternalEvidencePick | null
  /** Tells the layout whether the dialog is open, so the document chip can
   *  say "Als Fundstelle übernehmen" instead of "+ Als Aussage hinzufügen". */
  onOpenStateChange?: (open: boolean) => void
  /** The trigger, rendered OUTSIDE the Dialog (same reasoning as
   *  `RemoveStatementControl.renderTrigger`): the caller's button opens the
   *  dialog by callback. */
  renderTrigger: (open: () => void) => React.ReactNode
}

/**
 * "+ Aussage hinzufügen" (TUR-1445): record a statement the pipeline never
 * extracted. V1's own (deleted) `ManageStatements` form is the field spec —
 * Verordnung + Konformitätsstatus, plus the V2 additions: blanket/subset
 * Geltung, the reviewer's transcription, and an optional Fundstelle.
 *
 * THE FUNDSTELLE COMES FROM THE DOCUMENT ITSELF, never from a mode: the
 * dialog is `modal={false}` and pinned over the mask pane, so the PDF stays
 * selectable the whole time — selecting text there raises the floating chip
 * (`DocumentPane`), and confirming it lands here via `externalPick`. The
 * selection-first path (chip with no dialog open) opens this dialog
 * pre-filled. Outside clicks deliberately do NOT dismiss
 * (`onInteractOutside` prevented): selecting in the document IS the outside
 * interaction this dialog exists to receive.
 *
 * The server writes the interface group, the staged row and the `included`
 * provenance note in one gesture; the created card then renders, blocks
 * approve and carries its "Manuell ergänzt" chip like any other statement.
 */
export function AddStatementControl({
  declId,
  frameworks,
  activeFramework,
  nameOf,
  pool,
  isWorkbook,
  onCreated,
  externalPick = null,
  onOpenStateChange,
  renderTrigger,
}: AddStatementControlProps) {
  const { translate } = useTranslation()
  const createStatement = useCreateStatement()
  const [open, setOpen] = useState(false)
  const [framework, setFramework] = useState(activeFramework ?? "")
  const [verdict, setVerdict] = useState<Verdict | null>(null)
  const [scopeAll, setScopeAll] = useState(true)
  const [staged, setStaged] = useState<string[]>([])
  const [query, setQuery] = useState("")
  const [text, setText] = useState("")
  const [evidence, setEvidence] = useState<EvidencePick | null>(null)
  const [busy, setBusy] = useState(false)
  const [seenPickToken, setSeenPickToken] = useState(0)
  // The document pane's live box — the ONLY region that stays sharp beside
  // the dialog: the blur scrim is drawn as four bands AROUND it (top strip
  // with the app chrome, left half with the mask, thin right/bottom seams),
  // and the dialog centers over the left band. Measured from the live DOM
  // (the split is user-draggable) and re-measured on window resize and on
  // the pane's own resizes; null falls back to a full scrim + viewport-
  // centered dialog (a render with no document pane on screen).
  const [docRect, setDocRect] = useState<{
    top: number
    left: number
    right: number
    bottom: number
  } | null>(null)

  useEffect(() => {
    if (!open) return undefined
    const measure = () => {
      const doc = document.querySelector(".sv2-pane-doc")
      if (!doc) {
        setDocRect(null)
        return
      }
      const r = doc.getBoundingClientRect()
      setDocRect({ top: r.top, left: r.left, right: r.right, bottom: r.bottom })
    }
    measure()
    window.addEventListener("resize", measure)
    const doc = document.querySelector(".sv2-pane-doc")
    const observer = doc ? new ResizeObserver(measure) : null
    if (doc && observer) observer.observe(doc)
    return () => {
      window.removeEventListener("resize", measure)
      observer?.disconnect()
    }
  }, [open])

  useEffect(() => {
    onOpenStateChange?.(open)
  }, [open, onOpenStateChange])

  // A confirmed selection from the document: replace the Fundstelle when the
  // dialog stands open, else open fresh around it (selection-first entry).
  // Token-guarded so a re-render never re-applies an old pick over a field
  // the reviewer has since cleared.
  useEffect(() => {
    if (!externalPick || externalPick.token === seenPickToken) return
    setSeenPickToken(externalPick.token)
    if (!open) {
      setFramework(activeFramework ?? "")
      setVerdict(null)
      setScopeAll(true)
      setStaged([])
      setQuery("")
      setText("")
      setBusy(false)
      setOpen(true)
    }
    setEvidence(externalPick.pick)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalPick])

  const reset = () => {
    setFramework(activeFramework ?? "")
    setVerdict(null)
    setScopeAll(true)
    setStaged([])
    setQuery("")
    setText("")
    setEvidence(null)
    setBusy(false)
  }

  const openDialog = () => {
    reset()
    setOpen(true)
  }

  const toggleArticle = (articleNumber: string) => {
    setStaged((prev) =>
      prev.includes(articleNumber)
        ? prev.filter((n) => n !== articleNumber)
        : [...prev, articleNumber],
    )
  }

  // Same rules as ArticleSlot's scope editor: the field only earns its row
  // once the pool is too long to scan, and it narrows what RENDERS only —
  // `staged` (and so the write) keeps every ticked article, hidden or not.
  const searchable = pool.length > 5
  // The article browser's own matcher: every article field, not just number,
  // name and supplier code.
  const q = searchable ? query.trim().toLowerCase() : ""
  const visiblePool = q ? pool.filter((a) => articleMatches(a, [q])) : pool

  const canSubmit =
    framework !== "" &&
    verdict !== null &&
    (scopeAll || staged.length > 0) &&
    !busy

  const handleSubmit = async () => {
    if (!canSubmit || verdict === null) return
    setBusy(true)
    try {
      const created = await createStatement.mutateAsync({
        declarationId: declId,
        framework,
        verdict,
        scopeAll,
        articleNumbers: scopeAll ? [] : staged,
        statementText: text.trim() || null,
        evidence: evidence
          ? {
              quote: evidence.quote,
              page: evidence.page,
              occurrence: evidence.occurrence ?? null,
            }
          : null,
      })
      toast.success(translate("MC_DECL_V2_ADD_STATEMENT_SUCCESS"))
      setOpen(false)
      onCreated(created.framework, created.statementId)
    } catch {
      // `useCreateStatement`'s own onError already toasted the reason; the
      // dialog stays open with every field intact for a retry.
      setBusy(false)
      return
    }
    setBusy(false)
  }

  return (
    <>
      {renderTrigger(openDialog)}
      {/* The scrim: EVERYTHING blurs except the document pane (this
          dialog's input surface, kept sharp and selectable) and the dialog
          itself — four bands drawn around the pane's live box. Clicking any
          band closes, the scrim convention; clicking or selecting in the
          document deliberately does not (see onInteractOutside below). */}
      {open && (
        <div data-testid="add-statement-scrim" onClick={() => setOpen(false)}>
          {(docRect
            ? [
                { top: 0, left: 0, right: 0, height: docRect.top },
                { top: docRect.bottom, left: 0, right: 0, bottom: 0 },
                {
                  top: docRect.top,
                  left: 0,
                  width: docRect.left,
                  height: docRect.bottom - docRect.top,
                },
                {
                  top: docRect.top,
                  left: docRect.right,
                  right: 0,
                  height: docRect.bottom - docRect.top,
                },
              ]
            : [{ top: 0, left: 0, right: 0, bottom: 0 }]
          ).map((band, i) => (
            <div
              key={i}
              className="fixed z-40 bg-black/40 backdrop-blur-[2px]"
              style={band}
            />
          ))}
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen} modal={false}>
        {/* Centered over the LEFT half of the split (style.left wins over the
            default centering class), never over the document. min-w-0 down
            the chain: DialogContent is a grid, and one long unbreakable
            quote in the evidence chip would blow the intrinsic width past
            the dialog otherwise. */}
        <DialogContent
          className="max-w-md overflow-hidden"
          style={docRect !== null ? { left: docRect.left / 2 } : undefined}
          onInteractOutside={(e) => e.preventDefault()}
          // Same capture-phase swallow as the open-points popover: Escape
          // closes THIS dialog, never the whole workspace.
          onEscapeKeyDown={(e) => e.stopPropagation()}
        >
          <DialogHeader>
            <DialogTitle>
              {translate("MC_DECL_V2_ADD_STATEMENT_TITLE")}
            </DialogTitle>
          </DialogHeader>
          <p className="text-[12px] text-grey5">
            {translate("MC_DECL_OV_INCLUDED_SENTENCE")}
          </p>

          <div className="min-w-0 space-y-3">
            <label className="block">
              <span className="mb-1 block text-[11px] uppercase tracking-wide text-grey5">
                {translate("MC_DECL_V2_ADD_STATEMENT_FRAMEWORK")}
              </span>
              <Select value={framework} onValueChange={setFramework}>
                {/* A label element does not name a Radix trigger (it is not a
                    labelable control) — same explicit aria as VerdictSelect. */}
                <SelectTrigger
                  className="h-8 text-[13px]"
                  aria-label={translate("MC_DECL_V2_ADD_STATEMENT_FRAMEWORK")}
                >
                  <SelectValue
                    placeholder={translate(
                      "MC_DECL_V2_ADD_STATEMENT_FRAMEWORK_PICK",
                    )}
                  />
                </SelectTrigger>
                <SelectContent>
                  {frameworks.map((fw) => (
                    <SelectItem key={fw} value={fw} className="text-[13px]">
                      {nameOf(fw)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>

            <label className="block">
              <span className="mb-1 block text-[11px] uppercase tracking-wide text-grey5">
                {translate("MC_DECL_V2_ADD_STATEMENT_VERDICT")}
              </span>
              <VerdictSelect value={verdict} onChange={setVerdict} />
            </label>

            <div>
              <span className="mb-1 block text-[11px] uppercase tracking-wide text-grey5">
                {translate("MC_DECL_V2_ADD_STATEMENT_SCOPE")}
              </span>
              {/* GLOBAL tokens only, never sv2-* classes: this content
                  portals to document.body, outside `.sv2-root`'s variable
                  scope, where every sv2 token resolves empty (claude-bot,
                  PR #2056; same rule as RemoveStatementControl's dialog). */}
              <div className="flex gap-2" role="radiogroup">
                <button
                  type="button"
                  role="radio"
                  aria-checked={scopeAll}
                  className={cn(
                    "rounded-lg border px-3.5 py-2 text-[13px] transition-colors",
                    scopeAll
                      ? "border-attention text-white"
                      : "border-grey4 text-grey5 hover:text-white",
                  )}
                  onClick={() => setScopeAll(true)}
                >
                  {translate("MC_DECL_V2_UNIT_ARTICLES_ALL")}
                </button>
                <button
                  type="button"
                  role="radio"
                  aria-checked={!scopeAll}
                  className={cn(
                    "rounded-lg border px-3.5 py-2 text-[13px] transition-colors",
                    !scopeAll
                      ? "border-attention text-white"
                      : "border-grey4 text-grey5 hover:text-white",
                  )}
                  onClick={() => setScopeAll(false)}
                >
                  {translate("MC_DECL_V2_ADD_STATEMENT_SCOPE_LIST")}
                </button>
              </div>
              {!scopeAll && searchable && (
                <div className="relative mt-2">
                  <Search
                    className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-grey5"
                    aria-hidden="true"
                  />
                  <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={(e) => {
                      // Escape clears an active filter before it can close
                      // the dialog; an inactive field lets it bubble.
                      if (e.key === "Escape" && q) {
                        e.stopPropagation()
                        setQuery("")
                      }
                    }}
                    placeholder={translate(
                      "MC_DECL_V2_ARTS_SEARCH_PLACEHOLDER",
                    )}
                    aria-label={translate("MC_DECL_V2_ARTS_SEARCH_PLACEHOLDER")}
                    className="w-full rounded-md border border-grey4 bg-transparent py-1.5 pl-7 pr-2 text-[12.5px] text-white placeholder:text-grey5 focus:border-attention focus:outline-hidden"
                  />
                </div>
              )}
              {!scopeAll && (
                <div className="mt-2 max-h-40 space-y-0.5 overflow-y-auto rounded-md border border-grey4 p-2">
                  {pool.length === 0 && (
                    <p className="text-[12px] text-grey5">
                      {translate("MC_DECL_V2_ADD_STATEMENT_NO_POOL")}
                    </p>
                  )}
                  {q && visiblePool.length === 0 && (
                    <p className="text-[12px] text-grey5">
                      {translate("MC_DECL_V2_ARTS_SEARCH_EMPTY")}
                    </p>
                  )}
                  <TooltipProvider delayDuration={200}>
                    {visiblePool.map((article) => (
                      <label
                        key={article.articleNumber}
                        className="flex cursor-pointer items-baseline gap-2 rounded px-1 py-0.5 text-[12.5px] hover:bg-white/5"
                      >
                        <input
                          type="checkbox"
                          className="translate-y-px"
                          checked={staged.includes(article.articleNumber)}
                          onChange={() => toggleArticle(article.articleNumber)}
                        />
                        <span className="font-mono text-[11px] text-grey5">
                          {article.articleNumber}
                          {article.supplierCode &&
                            article.supplierCode !== article.articleNumber && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span> · {article.supplierCode}</span>
                                </TooltipTrigger>
                                <TooltipContent side="top">
                                  {translate("MC_DECL_SB_SUPPLIER_CODE")}
                                </TooltipContent>
                              </Tooltip>
                            )}
                        </span>
                        <span className="min-w-0 truncate">{article.name}</span>
                      </label>
                    ))}
                  </TooltipProvider>
                </div>
              )}
            </div>

            <label className="block">
              <span className="mb-1 block text-[11px] uppercase tracking-wide text-grey5">
                {translate("MC_DECL_V2_ADD_STATEMENT_TEXT")}
              </span>
              <Textarea
                rows={2}
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={translate("MC_DECL_V2_ADD_STATEMENT_TEXT_HINT")}
              />
            </label>

            {!isWorkbook && (
              <div>
                <span className="mb-1 block text-[11px] uppercase tracking-wide text-grey5">
                  {translate("MC_DECL_V2_ADD_STATEMENT_EVIDENCE")}
                </span>
                {evidence ? (
                  <div
                    className="flex min-w-0 max-w-full items-center gap-2 rounded-md border border-grey4 px-2 py-1.5 text-[12px]"
                    data-testid="picked-evidence"
                  >
                    <MapPin size={12} className="shrink-0 text-grey5" />
                    <span className="min-w-0 flex-1 truncate">
                      {evidence.quote}
                    </span>
                    <span className="shrink-0 text-grey5">
                      {translate("MC_DECL_V2_ADD_STATEMENT_EVIDENCE_PAGE", {
                        page: evidence.page,
                      })}
                    </span>
                    <button
                      type="button"
                      aria-label={translate(
                        "MC_DECL_V2_ADD_STATEMENT_EVIDENCE_REMOVE",
                      )}
                      className="shrink-0 text-grey5 hover:text-white"
                      onClick={() => setEvidence(null)}
                    >
                      <X size={13} />
                    </button>
                  </div>
                ) : (
                  // NOT a button: the affordance is the document itself. The
                  // dialog is non-modal, so the reviewer just selects the
                  // passage over there and confirms the chip.
                  <p className="text-[12px] text-grey5">
                    {translate("MC_DECL_V2_ADD_STATEMENT_EVIDENCE_HINT")}
                  </p>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <button
              type="button"
              className="px-3 py-1.5 text-[13px] text-grey5 hover:text-white"
              onClick={() => setOpen(false)}
            >
              {translate("MC_DECL_V2_ADD_STATEMENT_CANCEL")}
            </button>
            <button
              type="button"
              className="rounded-md bg-attention px-3 py-1.5 text-[13px] font-semibold text-black disabled:opacity-40"
              disabled={!canSubmit}
              onClick={handleSubmit}
            >
              {translate("MC_DECL_V2_ADD_STATEMENT_SUBMIT")}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
